// src/components/modals/EditTableModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, Save, Hash, Users, MapPin, Clock, ChevronDown } from 'lucide-react'
import TableService from '../../services/tableService'
import BranchService from '../../services/branchService'
import { TableModel, VALIDATION_RULES, TABLE_AVAILABILITY, AVAILABILITY_CONFIG } from '../../utils/tableModel'

const EditTableModal = ({ isOpen, onClose, onTableUpdated, table, restrictedBranchId = null, isBranchRestricted = false }) => {
  const [formData, setFormData] = useState({
    number: '',
    branch: '',
    capacity: '',
    availability: TABLE_AVAILABILITY.AVAILABLE,
    isActive: true
  })
  
  const [branches, setBranches] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branchesLoading, setBranchesLoading] = useState(false)

  // Populate form when table changes
  useEffect(() => {
    if (isOpen && table) {
      const branchValue = isBranchRestricted && restrictedBranchId 
        ? restrictedBranchId 
        : (typeof table.branch === 'object' ? table.branch._id : table.branch || '')
      
      setFormData({
        number: table.number || '',
        branch: branchValue,
        capacity: table.capacity?.toString() || '',
        availability: table.availability || TABLE_AVAILABILITY.AVAILABLE,
        isActive: table.isActive !== undefined ? table.isActive : true
      })
      setErrors({})
      fetchBranches()
    }
  }, [isOpen, table, isBranchRestricted, restrictedBranchId])

  // Fetch branches for dropdown
  const fetchBranches = async () => {
    try {
      setBranchesLoading(true)
      console.log('🏢 Fetching branches for table editing...')
      
      const response = await BranchService.getAllBranches()
      
      if (response && response.success) {
        const branchesData = response.data?.branches || response.data?.docs || response.data || []
        console.log('🏢 Branches fetched for dropdown:', branchesData.length)
        
        // Filter only active branches
        let activeBranches = branchesData.filter(branch => branch.isActive)
        
        // Filter to only manager's branch if restricted
        if (isBranchRestricted && restrictedBranchId) {
          activeBranches = activeBranches.filter(branch => branch._id === restrictedBranchId)
          console.log('🔒 Filtered to manager\'s branch only for table edit')
        }
        
        setBranches(activeBranches)
      } else {
        console.warn('⚠️ Failed to fetch branches:', response)
        setBranches([])
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
      setBranches([])
    } finally {
      setBranchesLoading(false)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle toggle switch
  const handleToggleSwitch = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }))
  }

  // Validate form
  const validateForm = () => {
    const validation = TableModel.validate(formData, false) // Pass false for isCreation (this is an update)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    if (!table?._id) {
      console.error('❌ No table ID provided for update')
      setErrors({ submit: 'Table ID is missing' })
      return
    }

    try {
      setLoading(true)
      console.log('🪑 Updating table with data:', formData)
      
      const response = await TableService.updateTable(table._id, formData)
      
      if (response && response.success) {
        console.log('✅ Table updated successfully:', response.data)
        
        // Notify parent component
        onTableUpdated(response.data.table)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Table update failed:', response)
        setErrors({ submit: response.message || 'Failed to update table' })
      }
    } catch (error) {
      console.error('❌ Error updating table:', error)
      setErrors({ submit: error.message || 'Failed to update table. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {/* <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Save className="h-5 w-5 text-orange-600" />
              </div> */}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Edit Table</h2>
              {table && (
                <p className="text-sm text-gray-500">
                  Table {table.number}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Submit Error */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Table Number */}
          <div>
            <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-1">
              Table Number *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Hash className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="number"
                name="number"
                type="text"
                placeholder="Enter table number (e.g., T001)"
                value={formData.number}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.number ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                disabled={loading}
                maxLength={VALIDATION_RULES.number.maxLength}
              />
            </div>
            {errors.number && (
              <p className="mt-1 text-sm text-red-600">{errors.number}</p>
            )}
          </div>

          {/* Branch Selection */}
<div>
  <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-1">
    Branch *
  </label>
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <MapPin className="h-4 w-4 text-gray-400" />
    </div>
    <select
      id="branch"
      name="branch"
      value={formData.branch}
      onChange={handleInputChange}
      className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white appearance-none ${
        errors.branch ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
      } ${isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      disabled={loading || branchesLoading || isBranchRestricted}
    >
      <option value="">
        {branchesLoading ? 'Loading branches...' : 'Select Branch'}
      </option>
      {branches.map(branch => (
        <option key={branch._id} value={branch._id}>
          {branch.name} ({branch.type})
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
  </div>
  {errors.branch && (
    <p className="mt-1 text-sm text-red-600">{errors.branch}</p>
  )}
</div>

          {/* Capacity */}
          <div>
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
              Capacity *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Users className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="capacity"
                name="capacity"
                type="number"
                placeholder="Enter seating capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.capacity ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                disabled={loading}
                min={VALIDATION_RULES.capacity.min}
                max={VALIDATION_RULES.capacity.max}
              />
            </div>
            {errors.capacity && (
              <p className="mt-1 text-sm text-red-600">{errors.capacity}</p>
            )}
          </div>

          {/* Availability Status */}
<div>
  <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
    Availability *
  </label>
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <Clock className="h-4 w-4 text-gray-400" />
    </div>
    <select
      id="availability"
      name="availability"
      value={formData.availability}
      onChange={handleInputChange}
      className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none ${
        errors.availability ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
      }`}
      disabled={loading}
    >
      {Object.values(TABLE_AVAILABILITY).map(availability => (
        <option key={availability} value={availability}>
          {AVAILABILITY_CONFIG[availability].icon} {AVAILABILITY_CONFIG[availability].label}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
  </div>
  {errors.availability && (
    <p className="mt-1 text-sm text-red-600">{errors.availability}</p>
  )}
</div>

          {/* Status Toggle - Orange Switch */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Status
              </label>
              {/* <p className="text-xs text-gray-500">
                {formData.isActive ? 'Table is active and available for use' : 'Table is inactive and hidden'}
              </p> */}
            </div>
            <div className="flex items-center">
              <button
                type="button"
                onClick={handleToggleSwitch}
                disabled={loading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  formData.isActive ? 'bg-orange-600' : 'bg-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              {/* <span className="ml-3 text-sm text-gray-700">
                {formData.isActive ? 'Active' : 'Inactive'}
              </span> */}
            </div>
          </div>

          {/* Table Info */}
          {table && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Table Information</h4>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                <div>
                  <span className="font-medium">Created:</span>
                  <br />
                  {table.createdAt ? new Date(table.createdAt).toLocaleDateString() : 'Unknown'}
                </div>
                <div>
                  <span className="font-medium">Updated:</span>
                  <br />
                  {table.updatedAt ? new Date(table.updatedAt).toLocaleDateString() : 'Unknown'}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || branches.length === 0}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditTableModal